import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import cx from "classnames";
import { Hooks, Util } from "@xt/xtbase";
import { useRouter } from "next/router";
import { Modal, message } from "antd";
import { Modal as AppPasskeyModal } from "@xt/Passkey";
import store from "store";
import ModelAlert from "components/Antd/Modal/Alert";
import C2CModal from "components/App/C2CModal";
import XtPagination, { PaginationProps, PaginationRefProps } from "@/components/Xt/Pagination/Simple";
import useNextTick from "@/hooks/useNextTick";
import { waitRes } from "utils/axios";
import { axiosThrottle } from "utils/method";
import { get_getFa, put_getFa, delete_getFa } from "api/uaapi/user";
import { get_fastWithdrawStatus } from "@/api/xt4/wallet";
import { delete_clientDevice, get_safeLog, get_clientDevice, delete_clientDeviceClear } from "@/api/uaapi/uaa";
import { IFaDataItem } from "store/user";
import {
  IconNavShieldCheck,
  IconSecMail,
  IconSecPhone,
  IconSecKeyRound,
  IconSecZap,
  IconSecLock,
  IconSecTrash,
  IconSecLaptop,
  IconNavLogOut,
  IconSecChevronRight,
} from "@/components/icons/accountIcons";
import CloseAccountModal from "./_CMPT/Modal/CloseAccount";
import clearEquipImg from "@/assets/images/account/icon-trashbin-darkmode.png";
import styles from "./securityCenter.module.scss";
import modalStyles from "./securityModals.module.scss";

const { useTranslation, useDocumentWidth } = Hooks;
const { moment, getUrl } = Util;

type SecurityLevel = "Low" | "Medium" | "High";
type ActivityTab = "log" | "devices";

interface LogRow {
  id: number;
  time: number;
  type: string;
  terminalName: string;
  terminal: number;
}

interface DeviceRow {
  id: number;
  clientDeviceId: string;
  clientDeviceName: string;
  country?: string;
  province?: string;
  city?: string;
  times?: number;
  ip?: string;
}

function maskEmail(email: string) {
  const [local, domain] = email.split("@");
  if (!domain) return email;
  const head = local.slice(0, 2);
  return `${head}${"*".repeat(Math.max(2, local.length - 2))}@${domain}`;
}

function maskMobile(mobile: string, countryCode?: string | number) {
  const code = countryCode ? `+${countryCode} ` : "";
  const text = `${mobile}`;
  const masked =
    text.length > 7 ? text.replace(/(\d{3})\d*(\d{4})/, "$1***$2") : text.replace(/(\d{2})\d*(\d{2})/, "$1***$2");
  return `${code}${masked}`;
}

function formatLocation(parts: Array<string | undefined>) {
  const filtered = parts.filter(Boolean);
  return filtered.length ? filtered.join("-") : "Unknown";
}

function CircleScore({ value, max }: { value: number; max: number }) {
  const r = 28;
  const circ = 2 * Math.PI * r;
  const dash = max > 0 ? (value / max) * circ : 0;
  return (
    <div className={styles.circleScore}>
      <svg viewBox="0 0 72 72" className={styles.circleSvg} aria-hidden>
        <circle cx="36" cy="36" r={r} className={styles.circleTrack} />
        <circle cx="36" cy="36" r={r} className={styles.circleFill} strokeDasharray={`${dash} ${circ}`} />
      </svg>
      <div className={styles.circleLabel}>
        {value}
        <span className={styles.circleMax}>/{max}</span>
      </div>
    </div>
  );
}

function PillBtn({
  children,
  danger,
  onClick,
}: {
  children: React.ReactNode;
  danger?: boolean;
  onClick?: () => void;
}) {
  return (
    <button type="button" className={cx(styles.pillBtn, danger && styles.pillBtnDanger)} onClick={onClick}>
      {children}
    </button>
  );
}

function GreenBadge({ children }: { children: React.ReactNode }) {
  return <span className={styles.greenBadge}>{children}</span>;
}

const LEVEL_COLOR: Record<SecurityLevel, string> = {
  Low: "#ef4444",
  Medium: "#f59e0b",
  High: "#99ff6d",
};
const LEVEL_BARS: Record<SecurityLevel, number> = { Low: 1, Medium: 2, High: 3 };

function StrengthBars({ level, label }: { level: SecurityLevel; label: string }) {
  const filled = LEVEL_BARS[level];
  const color = LEVEL_COLOR[level];
  return (
    <span className={styles.strengthBars}>
      {[1, 2, 3].map((i) => (
        <span
          key={i}
          className={styles.strengthBar}
          style={{ background: i <= filled ? color : "rgba(255,255,255,0.15)" }}
        />
      ))}
      <span className={styles.strengthLabel} style={{ color }}>
        {label}
      </span>
    </span>
  );
}

function SecRow({
  icon: Icon,
  title,
  badge,
  desc,
  sub,
  action,
  last,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: React.ReactNode;
  badge?: React.ReactNode;
  desc?: React.ReactNode;
  sub?: React.ReactNode;
  action?: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div className={cx(styles.row, last && styles.rowLast)}>
      <span className={styles.rowIcon} aria-hidden>
        <Icon />
      </span>
      <div className={styles.rowMain}>
        <div className={styles.rowTitleLine}>
          <span className={styles.rowTitle}>{title}</span>
          {badge}
        </div>
        {desc ? <div className={styles.rowDesc}>{desc}</div> : null}
        {sub ? <div className={styles.rowSub}>{sub}</div> : null}
      </div>
      {action ? <div className={styles.rowAction}>{action}</div> : null}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      <div className={cx(styles.card, styles.sectionCard)}>{children}</div>
    </section>
  );
}

const SecurityCenter: React.FC = () => {
  const t = useTranslation();
  const router = useRouter();
  const nextTick = useNextTick();
  const docW = useDocumentWidth();
  const { userInfo, notSub, updateState, getUserInfo } = store.user;

  const [faDatas, setFaDatas] = useState<IFaDataItem[]>([]);
  const [fastWithdrawEnabled, setFastWithdrawEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState<ActivityTab>("log");
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [c2cVisible, setC2cVisible] = useState(false);
  const [pushName, setPushName] = useState("");
  const [faType, setFaType] = useState("");
  const [faBizType, setFaBizType] = useState(127);
  const [codeType, setCodeType] = useState("USER_2033");
  const [faModalOpen, setFaModalOpen] = useState(false);
  const [faIsOpen, setFaIsOpen] = useState(false);

  const [pageIndex, setPageIndex] = useState(1);
  const [limit] = useState(10);
  const [fromId, setFromId] = useState("");
  const [direction, setDirection] = useState("NEXT");
  const [hasPrev, setHasPrev] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [logData, setLogData] = useState<LogRow[]>([]);
  const [deviceData, setDeviceData] = useState<DeviceRow[]>([]);
  const [deleteDevice, setDeleteDevice] = useState<DeviceRow | null>(null);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const paginationRef = useRef<WithNull<PaginationRefProps>>(null);

  const authLen = useMemo(() => faDatas.filter((item) => !!item.enable).length, [faDatas]);

  const getAuthData = useCallback((type: string) => faDatas.find((item) => item.type === type)?.enable || 0, [faDatas]);
  const getAuthOpen = useCallback(
    (type: string) => {
      const item = faDatas.find((i) => i.type === type);
      return !!item?.bind && !item.enable;
    },
    [faDatas],
  );
  const getIsBind = useCallback((type: string) => !!faDatas.find((item) => item.type === type)?.bind, [faDatas]);

  const passwordLevel: SecurityLevel = useMemo(() => {
    const n = userInfo?.loginPwdLevel || 0;
    if (n < 3) return "Low";
    if (n === 3) return "Medium";
    return "High";
  }, [userInfo?.loginPwdLevel]);

  const passwordLevelLabel = useMemo(() => {
    if (passwordLevel === "Low") return t("account.Low");
    if (passwordLevel === "Medium") return t("account.Medium");
    return t("account.High");
  }, [passwordLevel, t]);

  const securityItems = useMemo(() => {
    const items = [
      userInfo?.googleAuth === 1,
      !!userInfo?.email,
      !!userInfo?.mobile,
      !!userInfo?.antiFishingCode,
      fastWithdrawEnabled,
    ];
    if (userInfo?.userAccountLevel! > 1) {
      return items.filter((_, idx) => idx !== 1 && idx !== 2);
    }
    return items;
  }, [userInfo, fastWithdrawEnabled]);

  const connected = securityItems.filter(Boolean).length;
  const total = securityItems.length;
  const remaining = Math.max(0, total - connected);

  const getFaDatas = useCallback(async () => {
    const { data, err } = await waitRes(get_getFa, {});
    if (err) return;
    updateState({ faData: data });
    setFaDatas(data || []);
  }, [updateState]);

  const getDevices = useCallback(async () => {
    const { data: res, err } = await waitRes(get_clientDevice, { params: { fromId, limit, direction } });
    if (err) return;
    setDeviceData((res?.items || []) as DeviceRow[]);
    setHasPrev(!!res?.hasPrev);
    setHasNext(!!res?.hasNext);
    nextTick(() => paginationRef.current?.enableNext(!res?.hasNext));
  }, [direction, fromId, limit]);

  useEffect(() => {
    getUserInfo();
    getFaDatas();
    (async () => {
      const { data, err } = await waitRes(get_fastWithdrawStatus);
      if (!err) setFastWithdrawEnabled(!!data);
    })();
  }, [getFaDatas, getUserInfo]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const fetcher = activeTab === "log" ? get_safeLog : get_clientDevice;
      const { data: res, err } = await waitRes(fetcher, { params: { fromId, limit, direction } });
      if (err || cancelled) return;
      if (activeTab === "log") {
        setLogData((res?.items || []) as LogRow[]);
      } else {
        setDeviceData((res?.items || []) as DeviceRow[]);
      }
      setHasPrev(!!res?.hasPrev);
      setHasNext(!!res?.hasNext);
      nextTick(() => paginationRef.current?.enableNext(!res?.hasNext));
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, pageIndex, fromId, direction, limit]);

  const routeWithC2c = (path: string) => {
    setPushName(path);
    setC2cVisible(true);
  };

  const changeVerify = () => {
    router.push(pushName);
  };

  const openFaType = (type: string) => {
    setFaType(type);
    setFaModalOpen(true);
    setFaIsOpen(true);
    setCodeType("USER_2034");
    setFaBizType(127);
  };

  const getVerify = (type: string) => {
    setFaType(type);
    setFaIsOpen(false);
    setFaBizType(128);
    setCodeType("USER_2033");
    if (authLen < 3) {
      const gaAuth = getAuthData("GA");
      const mobileAuth = getAuthData("MOBILE");
      const emailAuth = getAuthData("EMAIL");
      let bindItem = {} as IFaDataItem;
      let info = "";
      let url = "";
      if (gaAuth && mobileAuth) {
        info = t("account.authEmailTips");
        bindItem = faDatas.find((item) => item.type === "EMAIL")!;
        url = "/accounts/user/email";
        setFaType("EMAIL");
      } else if (gaAuth && emailAuth) {
        info = t("account.authMobileTips");
        bindItem = faDatas.find((item) => item.type === "MOBILE")!;
        url = "/accounts/user/mobile";
        setFaType("MOBILE");
      } else if (mobileAuth && emailAuth) {
        bindItem = faDatas.find((item) => item.type === "GA")!;
        info = t("account.authGaTips");
        url = "/accounts/user/google-authenticator";
        setFaType("GA");
      }
      ModelAlert.confirm({
        center: true,
        className: modalStyles.spikexAlert,
        content: info,
        okText: t("account.goEnable"),
        cancelText: t("account.cancel"),
        onOk: () => {
          if (!bindItem.bind) router.push(url);
          else {
            setFaBizType(127);
            setCodeType("USER_2034");
            setFaIsOpen(true);
            setFaModalOpen(true);
          }
        },
      });
    } else {
      setFaModalOpen(true);
    }
  };

  const submitFaForm = axiosThrottle(async (unlock, { faResultId }) => {
    const method = faIsOpen ? put_getFa : delete_getFa;
    const { err } = await waitRes(method, {
      errorPop: true,
      successPop: true,
      data: { faResultId, type: faType },
    });
    unlock();
    if (err) return;
    setFaModalOpen(false);
    getFaDatas();
  });

  const renderPhoneAction = () => {
    if (!userInfo?.mobile) {
      return <PillBtn onClick={() => router.push("/accounts/user/mobile")}>{t("account.Set") || "Set up"}</PillBtn>;
    }
    const buttons: React.ReactNode[] = [];
    if (getAuthOpen("MOBILE")) {
      buttons.push(
        <PillBtn key="open" onClick={() => openFaType("MOBILE")}>
          {t("cmpt.toOpen")}
        </PillBtn>,
      );
    }
    if (!!getAuthData("MOBILE") && authLen > 1 && getIsBind("MOBILE")) {
      buttons.push(
        <PillBtn key="close" onClick={() => getVerify("MOBILE")}>
          {t("account.closeVerify")}
        </PillBtn>,
      );
    }
    // 与移动端 AccountSet 一致：已绑定且账号等级≥2 或已启用≥2 种验证时展示更换，不因关闭/开启验证把 Change 挤掉
    if (userInfo?.userAccountLevel === 2 || authLen >= 2) {
      buttons.push(
        <PillBtn key="change" onClick={() => routeWithC2c("/accounts/user/mobile")}>
          {t("account.change")}
        </PillBtn>,
      );
    }
    if (!buttons.length) return null;
    return buttons;
  };

  /** 邮箱操作 — 对齐移动端 AccountSet：未绑定时可绑定；已绑定需满足安全条件才可更换 */
  const renderEmailAction = () => {
    if (!userInfo?.email) {
      return <PillBtn onClick={() => router.push("/accounts/user/email")}>{t("account.Set") || "Set up"}</PillBtn>;
    }
    if (userInfo?.userAccountLevel === 2 || authLen >= 2) {
      return <PillBtn onClick={() => routeWithC2c("/accounts/user/email")}>{t("account.change")}</PillBtn>;
    }
    return null;
  };

  const authLastIsPhone = !(userInfo?.userAccountLevel! > 1);

  const renderGaAction = () => {
    if (userInfo?.googleAuth === 0) {
      return <PillBtn onClick={() => router.push("/accounts/user/google-authenticator")}>{t("account.Set") || "Set up"}</PillBtn>;
    }
    if (!!getAuthData("GA") && authLen > 1 && getIsBind("GA")) {
      return <PillBtn onClick={() => getVerify("GA")}>{t("account.closeVerify")}</PillBtn>;
    }
    if (getAuthOpen("GA")) {
      return <PillBtn onClick={() => openFaType("GA")}>{t("cmpt.toOpen")}</PillBtn>;
    }
    if (userInfo?.googleAuth === 1) {
      return <PillBtn onClick={() => routeWithC2c("/accounts/user/google-authenticator")}>{t("account.change")}</PillBtn>;
    }
    return <PillBtn onClick={() => router.push("/accounts/user/google-authenticator")}>{t("account.Set") || "Set up"}</PillBtn>;
  };

  const handleTabChange = (tab: ActivityTab) => {
    setActiveTab(tab);
    setPageIndex(1);
    setFromId("");
    setDirection("NEXT");
  };

  const handlePreBtn: PaginationProps["prepage"] = (cb) => {
    const source = activeTab === "log" ? logData : deviceData;
    setPageIndex((p) => p - 1);
    setFromId(`${source[0]?.id || ""}`);
    setDirection("PREV");
    cb?.();
  };

  const handleNextBtn: PaginationProps["nextpage"] = (cb) => {
    const source = activeTab === "log" ? logData : deviceData;
    setPageIndex((p) => p + 1);
    setFromId(`${source[source.length - 1]?.id || ""}`);
    setDirection("NEXT");
    cb?.();
  };

  const handleDeleteDevice = async () => {
    if (!deleteDevice) return;
    const { err } = await waitRes(delete_clientDevice, {
      errorPop: true,
      data: {
        clientDeviceId: deleteDevice.clientDeviceId,
        clientDeviceName: deleteDevice.clientDeviceName,
      },
    });
    if (err) return;
    message.success(t("account.success"));
    setDeleteDevice(null);
    getDevices();
    const id = localStorage.getItem("client-device-id") || "";
    if (+id === deleteDevice.id) localStorage.removeItem("client-device-id");
  };

  const handleDeleteAll = async () => {
    const { err } = await waitRes(delete_clientDeviceClear, { errorPop: true });
    if (err) return;
    message.success(t("account.success"));
    setDeleteAllOpen(false);
    getDevices();
    localStorage.removeItem("client-device-id");
  };

  const currentDeviceId = typeof window !== "undefined" ? localStorage.getItem("client-device-id") : "";
  const showPagination = !!hasNext || !!hasPrev;
  const verifyDesc = t("account.UsedForSecurityVerifications");

  return (
    <div className={styles.page}>
      <div className={cx(styles.card, styles.hero)}>
        <CircleScore value={connected} max={total} />
        <div className={styles.heroBody}>
          <div className={styles.heroTitle}>{t("account.secSecureAccount") || "Secure your account"}</div>
          <div className={styles.heroDesc}>
            {(t("account.secImproveHint") || "Set up {n} more security measure(s) to better protect your account.").replace(
              "{n}",
              String(remaining),
            )}
          </div>
          <button type="button" className={styles.heroBtn}>
            {t("account.secImproveBtn") || "Improve security"}
          </button>
        </div>
      </div>

      <Section title={t("account.secAuthMethods") || "Authentication methods"}>
        <SecRow
          icon={IconNavShieldCheck}
          title={t("account.GoogleAuthenticator")}
          badge={<GreenBadge>{t("account.recommend") || "Recommended"}</GreenBadge>}
          desc={verifyDesc}
          sub={userInfo?.googleAuth === 0 ? t("account.noBinding") : undefined}
          action={renderGaAction()}
          last={!authLastIsPhone}
        />
        {!(userInfo?.userAccountLevel! > 1) && (
          <SecRow
            icon={IconSecMail}
            title={t("account.email")}
            desc={verifyDesc}
            sub={userInfo?.email ? maskEmail(userInfo.email) : t("account.noBinding")}
            action={renderEmailAction()}
          />
        )}
        {!(userInfo?.userAccountLevel! > 1) && (
          <SecRow
            icon={IconSecPhone}
            title={t("account.phone")}
            desc={verifyDesc}
            sub={
              userInfo?.mobile
                ? maskMobile(`${userInfo.mobile}`, userInfo.countryCode || userInfo.mobileCountryCode)
                : t("account.noBinding")
            }
            action={renderPhoneAction()}
            last
          />
        )}
      </Section>

      {notSub && (
        <Section title={t("account.secAdvanced") || "Advanced security"}>
          <SecRow
            icon={IconSecZap}
            title={t("withdrawal.currency") || "Quick Withdrawal"}
            desc={t("withdrawal.currencyTips")}
            action={
              <PillBtn onClick={() => { window.location.href = getUrl("/wallet/account/common/address"); }}>
                {fastWithdrawEnabled ? t("account.change") : t("account.Set") || "Setting"}
              </PillBtn>
            }
          />
          <SecRow
            icon={IconSecKeyRound}
            title={t("account.AntiPhishingCode")}
            desc={t("account.sentToYouContains")}
            sub={!userInfo?.antiFishingCode ? t("account.noBinding") : undefined}
            action={
              <PillBtn onClick={() => router.push("/accounts/user/anti-phishing")}>
                {userInfo?.antiFishingCode ? t("account.change") : t("account.Set") || "Set up"}
              </PillBtn>
            }
          />
          <SecRow
            icon={IconSecLock}
            title={t("account.loginPwd")}
            badge={<StrengthBars level={passwordLevel} label={passwordLevelLabel} />}
            desc={t("account.UsedforsecurityVerification")}
            action={<PillBtn onClick={() => routeWithC2c("/accounts/user/login-password")}>{t("account.change")}</PillBtn>}
            last
          />
        </Section>
      )}

      {notSub && (
        <Section title={t("account.secAccountMgmt") || "Account management"}>
          <SecRow
            icon={IconSecTrash}
            title={t("account.closeAccount")}
            desc={`${t("account.pageTip")}, ${t("account.pageTip2")}`}
            action={<PillBtn danger onClick={() => setCloseModalOpen(true)}>{t("account.close") || "Delete"}</PillBtn>}
            last
          />
        </Section>
      )}

      <Section title={t("account.secDevicesActivities") || "Devices and activities"}>
        <div className={styles.tabsBar}>
          {(["log", "devices"] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              className={cx(styles.tabBtn, activeTab === tab && styles.tabBtnActive)}
              onClick={() => handleTabChange(tab)}
            >
              {tab === "log" ? t("account.LoginLog") : t("newAccount.equipmentManage") || "Device Management"}
            </button>
          ))}
          {activeTab === "devices" && deviceData.length > 0 && docW > 768 ? (
            <button type="button" className={styles.clearAllBtn} onClick={() => setDeleteAllOpen(true)}>
              <img src={clearEquipImg} alt="" />
              {t("newAccount.clearAll")}
            </button>
          ) : null}
        </div>

        {activeTab === "log" && (
          <>
            {logData.length === 0 ? (
              <div className={styles.emptyState}>—</div>
            ) : (
              <table className={styles.logTable}>
                <thead className={styles.logHead}>
                  <tr>
                    <th>{t("account.time")}</th>
                    <th>{t("account.ActionType")}</th>
                    <th>{t("account.OperatingTerminal")}</th>
                  </tr>
                </thead>
                <tbody>
                  {logData.map((row) => {
                    const terminal = `${row.terminalName || ""}`.toLowerCase();
                    const isApp = terminal.includes("app") || row.terminal === 2;
                    return (
                      <tr key={row.id} className={styles.logRow}>
                        <td className={styles.logTime}>{moment(row.time).formatMs()}</td>
                        <td className={styles.logAction}>{row.type === "login" ? t("app.login") : row.type}</td>
                        <td>
                          <span className={cx(styles.terminalBadge, isApp ? styles.terminalApp : styles.terminalWeb)}>
                            {isApp ? "app" : "web"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}

        {activeTab === "devices" && (
          <>
            {deviceData.length === 0 ? (
              <div className={styles.emptyState}>—</div>
            ) : (
              deviceData.map((device) => {
                const isCurrent = currentDeviceId && +currentDeviceId === device.id;
                const location = formatLocation([device.country, device.province, device.city]);
                const lastActive =
                  typeof device.times === "number"
                    ? new Date(device.times).toLocaleString().replaceAll("/", "-")
                    : device.ip || "";
                return (
                  <div key={device.id} className={styles.deviceRow}>
                    <span className={styles.rowIcon} aria-hidden>
                      <IconSecLaptop />
                    </span>
                    <div className={styles.rowMain}>
                      <div className={styles.deviceNameLine}>
                        {device.clientDeviceName}
                        {isCurrent ? (
                          <span className={styles.currentBadge}>{t("account.secThisDevice") || "This device"}</span>
                        ) : null}
                      </div>
                      <div className={styles.deviceMeta}>
                        {location}
                        {lastActive ? ` · ${lastActive}` : ""}
                      </div>
                    </div>
                    {isCurrent ? (
                      <IconSecChevronRight className={styles.chevronMuted} />
                    ) : (
                      <button type="button" className={styles.revokeBtn} onClick={() => setDeleteDevice(device)}>
                        <IconNavLogOut />
                        {t("account.secRevoke") || "Revoke"}
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </>
        )}

        {showPagination && (
          <div className={styles.pagination}>
            <XtPagination
              ref={paginationRef}
              pageIndex={pageIndex}
              prepage={handlePreBtn}
              nextpage={handleNextBtn}
            />
          </div>
        )}
      </Section>

      <AppPasskeyModal
        faBizType={faBizType}
        dynamicCodeType={codeType}
        successCallback={submitFaForm}
        open={faModalOpen}
        onClose={() => setFaModalOpen(false)}
        isDark={store.app.isDark}
        userInfo={store.user.userInfo}
      />
      <C2CModal
        visible={c2cVisible}
        changeVisible={() => setC2cVisible(false)}
        changeVerify={changeVerify}
        modalClassName={modalStyles.spikexModal}
        variant="spikex"
      />
      <CloseAccountModal
        visible={closeModalOpen}
        title={t("account.closeAccount")}
        closemodal={() => setCloseModalOpen(false)}
        modalClassName={modalStyles.spikexModal}
        variant="spikex"
      />

      <Modal
        className={modalStyles.spikexModal}
        width={400}
        open={!!deleteDevice}
        title={t("newAccount.deleteEquipment")}
        onCancel={() => setDeleteDevice(null)}
        onOk={handleDeleteDevice}
        cancelText={t("newAccount.cancel")}
        okText={t("newAccount.isDelete")}
        okButtonProps={{ danger: true }}
      >
        <div>
          <span>{t("newAccount.equipments")}: </span>
          <span>{deleteDevice?.clientDeviceName}</span>
        </div>
      </Modal>

      <Modal
        className={modalStyles.spikexModal}
        width={400}
        open={deleteAllOpen}
        title={t("newAccount.clearAll")}
        onCancel={() => setDeleteAllOpen(false)}
        onOk={handleDeleteAll}
        cancelText={t("newAccount.cancel")}
        okText={t("newAccount.clearAllTrue")}
        okButtonProps={{ danger: true }}
      >
        <div>
          <span>{t("newAccount.clearAllTips")}</span>
        </div>
      </Modal>
    </div>
  );
};

export default observer(SecurityCenter);
